// 防护能力、按客户类型的解决方案、价格与套餐对比（纯数据，前端和构建脚本共用）

// ---------- 防护能力 ----------
export const CAPABILITIES = [
  { id: 'endpoint', zh: { name: '办公终端安全', desc: 'EDR 部署与策略调优、设备管控、离职回收' }, en: { name: 'Endpoint security', desc: 'EDR rollout and tuning, device control, offboarding' } },
  { id: 'identity', zh: { name: '账号与身份安全', desc: '统一登录、二次验证、最小权限' }, en: { name: 'Identity & access', desc: 'SSO, 2FA, least privilege' } },
  { id: 'zerotrust', zh: { name: '零信任访问', desc: '内部系统不对公网开放，按身份和设备授权访问' }, en: { name: 'Zero-trust access', desc: 'Internal systems kept off the internet, access granted by identity and device' } },
  { id: 'network', zh: { name: '网络与内网安全', desc: '网络隔离、横向移动检测、堡垒机' }, en: { name: 'Network & internal security', desc: 'Segmentation, lateral-movement detection, bastion hosts' } },
  { id: 'server', zh: { name: '服务器与云安全', desc: '主机加固、云配置检查、密钥管理' }, en: { name: 'Server & cloud security', desc: 'Host hardening, cloud config review, secrets management' } },
  { id: 'container', zh: { name: '容器 / K8s 安全', desc: '镜像扫描、集群配置、运行时防护' }, en: { name: 'Container / K8s security', desc: 'Image scanning, cluster config, runtime protection' } },
  { id: 'waf', zh: { name: 'WAF / API / DDoS 防护', desc: 'Web 与 API 攻击拦截、抗 DDoS' }, en: { name: 'WAF / API / DDoS', desc: 'Web and API attack blocking, DDoS mitigation' } },
  { id: 'wallet', zh: { name: '钱包与签名安全', desc: '多签 / MPC、审批流程、签名前核对' }, en: { name: 'Wallet & signing', desc: 'Multisig / MPC, approval flows, pre-signing checks' } },
  { id: 'monitor', zh: { name: '链上监控与告警', desc: '大额异动、权限变更实时告警' }, en: { name: 'On-chain monitoring', desc: 'Real-time alerts on large moves and permission changes' } },
  { id: 'audit', zh: { name: '代码审计', desc: '智能合约与后端代码安全审计' }, en: { name: 'Code audit', desc: 'Smart contract and backend code review' } },
  { id: 'pentest', zh: { name: '渗透测试', desc: '模拟真实攻击，找出可被利用的入口' }, en: { name: 'Penetration testing', desc: 'Simulated attacks to find exploitable entry points' } },
  { id: 'training', zh: { name: '安全意识与防钓鱼', desc: '真实案例培训、模拟钓鱼演练' }, en: { name: 'Security awareness', desc: 'Case-based training and phishing simulations' } },
  { id: 'ir', zh: { name: '应急响应', desc: '止损、取证、追踪、对外沟通' }, en: { name: 'Incident response', desc: 'Containment, forensics, tracing, comms' } },
]

// ---------- 按客户类型的解决方案 ----------
// plan: 推荐套餐（对应 PRICING.tiers 的 id 或 'consult'）
export const SOLUTIONS = [
  {
    id: 'exchange', plan: 'enterprise',
    caps: ['endpoint', 'identity', 'zerotrust', 'network', 'server', 'container', 'waf', 'wallet', 'monitor', 'pentest', 'training', 'ir'],
    zh: { name: '中心化交易所', who: '现货、合约交易所，以及交易所的钱包与风控团队', risks: ['热钱包私钥或签名系统被攻破', '员工被钓鱼，后台和运维权限失守', '撮合与 API 遭攻击或 DDoS'] },
    en: { name: 'Centralized exchanges', who: 'Spot and derivatives exchanges, and their wallet and risk teams', risks: ['Hot wallet keys or signing systems compromised', 'Staff phished, admin and ops access taken over', 'Matching engine and APIs attacked or DDoSed'] },
  },
  {
    id: 'defi', plan: 'pro',
    caps: ['audit', 'wallet', 'monitor', 'waf', 'identity', 'endpoint', 'server', 'ir'],
    zh: { name: 'DeFi / 项目方', who: '协议、公链生态项目、发币项目团队', risks: ['合约漏洞被利用，资金被一次性抽走', '部署者或管理员私钥泄露', '前端、DNS 被劫持，用户被骗签'] },
    en: { name: 'DeFi & protocols', who: 'Protocols, ecosystem projects and token teams', risks: ['Contract bugs exploited and funds drained', 'Deployer or admin keys leaked', 'Frontend or DNS hijacked, users tricked into signing'] },
  },
  {
    id: 'quant', plan: 'pro',
    caps: ['endpoint', 'server', 'zerotrust', 'network', 'identity', 'container', 'training', 'ir'],
    zh: { name: '量化 / 做市商', who: '量化交易团队、做市商、交易机器人团队', risks: ['交易服务器被入侵', '交易所 API Key 泄露，账户被恶意交易', '策略代码和数据外泄'] },
    en: { name: 'Quant & market makers', who: 'Quant funds, market makers and trading-bot teams', risks: ['Trading servers breached', 'Exchange API keys leaked and accounts abused', 'Strategy code and data exfiltrated'] },
  },
  {
    id: 'otc', plan: 'basic',
    caps: ['wallet', 'training', 'endpoint', 'identity', 'monitor', 'ir'],
    zh: { name: 'OTC / 支付服务商', who: 'OTC 商户、U 商、加密支付与出入金服务商', risks: ['收到被冻结的黑 U，资金被连带冻结', '客服、财务被钓鱼或社工', '转账地址被剪贴板木马替换'] },
    en: { name: 'OTC & payment providers', who: 'OTC desks, crypto payment and on/off-ramp providers', risks: ['Receiving frozen or tainted USDT', 'Support and finance staff phished or socially engineered', 'Payout addresses swapped by clipboard malware'] },
  },
  {
    id: 'wallet', plan: 'pro',
    caps: ['audit', 'pentest', 'server', 'container', 'waf', 'monitor', 'identity', 'ir'],
    zh: { name: '钱包服务商', who: '自托管钱包、MPC / 托管钱包服务商', risks: ['签名服务或密钥分片被攻破', '依赖库、构建流程被投毒（供应链攻击）', '客户端漏洞危及用户资产'] },
    en: { name: 'Wallet providers', who: 'Self-custody, MPC and custodial wallet providers', risks: ['Signing service or key shares compromised', 'Dependencies or build pipeline poisoned (supply chain)', 'Client bugs putting user assets at risk'] },
  },
  {
    id: 'fund', plan: 'basic',
    caps: ['wallet', 'endpoint', 'identity', 'training', 'monitor', 'ir'],
    zh: { name: '基金 / 资产管理', who: '加密基金、家族办公室、资产管理团队', risks: ['签名人电脑被控制，多签被骗签', '大额审批流程薄弱，一人就能转走钱', '合作方、投资人身份被冒充'] },
    en: { name: 'Funds & asset managers', who: 'Crypto funds, family offices and treasury teams', risks: ['Signer devices controlled, multisig tricked', 'Weak approval flows let one person move funds', 'Partners and LPs impersonated'] },
  },
  {
    id: 'studio', plan: 'basic',
    caps: ['endpoint', 'wallet', 'network', 'server', 'identity', 'training'],
    zh: { name: '空投 / 链上交互工作室', who: '多账户、多设备的链上交互与空投团队', risks: ['大量钱包私钥集中存放，一处泄露全盘损失', '脚本、浏览器插件被投毒', '设备、代理和云服务器被入侵'] },
    en: { name: 'Airdrop & on-chain studios', who: 'Multi-account, multi-device on-chain operation teams', risks: ['Keys for many wallets stored together, so one leak loses everything', 'Scripts and browser extensions poisoned', 'Devices, proxies and cloud servers breached'] },
  },
  {
    id: 'startup', plan: 'consult',
    caps: ['identity', 'endpoint', 'wallet', 'training', 'ir'],
    zh: { name: 'Web3 创业团队', who: '还没有专职安全负责人的早期团队', risks: ['账号、权限、密钥散落各处', '不知道钱可能从哪里丢', '出事时没人知道先做什么'] },
    en: { name: 'Web3 startups', who: 'Early teams without a dedicated security lead', risks: ['Accounts, permissions and keys scattered everywhere', 'No idea where funds could leak', 'Nobody knows what to do first when something happens'] },
  },
]

// ---------- 价格 ----------
// 对比表单元格：true = 包含，'opt' = 可选（另行报价），false = 不含，字符串 = 具体说明
export const PRICING = {
  entry: [
    { id: 'consult', zh: { name: '免费安全咨询', price: '¥0', unit: '30 分钟', desc: '线上聊 30 分钟，判断现在最该先补哪个洞。不推销，不绑定。' }, en: { name: 'Free consultation', price: '$0', unit: '30 min', desc: 'A 30-minute call to decide which gap to close first. No pitch, no commitment.' } },
    { id: 'assessment', slug: 'security-assessment', zh: { name: '资产安全体检', price: '¥3万', unit: '起 / 次', desc: '2–3 周找出钱最可能丢的地方，交付按优先级排好的整改清单。' }, en: { name: 'Security assessment', price: '$5k', unit: 'from / project', desc: 'In 2–3 weeks, find where money could leak, with a prioritized fix list.' } },
  ],
  tiers: [
    { id: 'basic', zh: { name: '基础版', price: '¥2万', unit: '/ 月', fit: '20 人以内的团队', highlights: ['兼职安全负责人', '终端、账号、零信任落地', '工作时间 4 小时响应'] }, en: { name: 'Basic', price: '$3k', unit: '/ month', fit: 'Teams up to 20 people', highlights: ['Fractional head of security', 'Endpoint, identity and zero-trust rollout', '4-hour response in business hours'] } },
    { id: 'pro', featured: true, zh: { name: '专业版', price: '¥4万', unit: '/ 月', fit: '20–60 人的团队', highlights: ['基础版全部内容', '内网、WAF、链上监控', '7×24 · 1 小时接手', '定期渗透测试与代码审计'] }, en: { name: 'Pro', price: '$6k', unit: '/ month', fit: 'Teams of 20–60 people', highlights: ['Everything in Basic', 'Network, WAF, on-chain monitoring', '24/7 · 1-hour takeover', 'Regular pentests and code audits'] } },
    { id: 'enterprise', zh: { name: '企业版', price: '定制', unit: '', fit: '交易所、钱包服务商等大规模团队', highlights: ['专业版全部内容', '容器 / K8s 全面防护', '7×24 · 30 分钟接手', '驻场支持'] }, en: { name: 'Enterprise', price: 'Custom', unit: '', fit: 'Exchanges, wallet providers and larger teams', highlights: ['Everything in Pro', 'Full container / K8s protection', '24/7 · 30-minute takeover', 'On-site support'] } },
  ],
  // 对比表：[分组, [[行名, 基础, 专业, 企业], ...]]
  compare: {
    zh: [
      ['基础权益', [
        ['适合团队规模', '20 人以内', '20–60 人', '不限'],
        ['每月服务投入', '2 人天', '4 人天', '按需'],
        ['覆盖终端 / 服务器', '各 20 台', '各 60 台', '不限'],
        ['响应时效', '工作时间 4 小时', '7×24 · 1 小时', '7×24 · 30 分钟'],
      ]],
      ['防护能力', [
        ['办公终端安全（EDR）', true, true, true],
        ['账号与身份安全', true, true, true],
        ['零信任访问', true, true, true],
        ['服务器与云安全', true, true, true],
        ['钱包与签名安全', true, true, true],
        ['网络与内网安全', 'opt', true, true],
        ['WAF / API / DDoS 防护', 'opt', true, true],
        ['链上监控与告警', 'opt', true, true],
        ['容器 / K8s 安全', false, 'opt', true],
        ['代码审计', false, '每季度 1 次', '按需'],
        ['渗透测试', false, '每半年 1 次', '每季度 1 次'],
        ['防钓鱼培训与演练', '每季度 1 次', '每月 1 次', '按需'],
        ['应急响应', '优先接手', '1 小时内接手', '30 分钟内接手'],
      ]],
      ['服务', [
        ['专属安全负责人', true, true, true],
        ['月度安全报告', true, true, true],
        ['季度复盘会', false, true, true],
        ['驻场支持', false, false, true],
      ]],
    ],
    en: [
      ['Basics', [
        ['Team size', 'Up to 20', '20–60', 'Unlimited'],
        ['Monthly effort', '2 person-days', '4 person-days', 'As needed'],
        ['Endpoints / servers covered', '20 each', '60 each', 'Unlimited'],
        ['Response time', '4h, business hours', '24/7 · 1h', '24/7 · 30 min'],
      ]],
      ['Protection', [
        ['Endpoint security (EDR)', true, true, true],
        ['Identity & access', true, true, true],
        ['Zero-trust access', true, true, true],
        ['Server & cloud security', true, true, true],
        ['Wallet & signing security', true, true, true],
        ['Network & internal security', 'opt', true, true],
        ['WAF / API / DDoS', 'opt', true, true],
        ['On-chain monitoring', 'opt', true, true],
        ['Container / K8s security', false, 'opt', true],
        ['Code audit', false, '1 per quarter', 'As needed'],
        ['Penetration testing', false, '1 per half-year', '1 per quarter'],
        ['Anti-phishing training & drills', '1 per quarter', '1 per month', 'As needed'],
        ['Incident response', 'Priority', 'Takeover within 1h', 'Takeover within 30 min'],
      ]],
      ['Service', [
        ['Dedicated head of security', true, true, true],
        ['Monthly security report', true, true, true],
        ['Quarterly review', false, true, true],
        ['On-site support', false, false, true],
      ]],
    ],
  },
  addons: [
    { slug: 'wallet-security', zh: { name: '钱包与资金架构设计', price: '¥5万 起 / 项目' }, en: { name: 'Wallet & treasury design', price: 'from $7k / project' } },
    { slug: 'smart-contract-audit', zh: { name: '智能合约审计', price: '按代码量报价' }, en: { name: 'Smart contract audit', price: 'Quoted by code size' } },
    { slug: null, zh: { name: '渗透测试', price: '按测试范围报价' }, en: { name: 'Penetration testing', price: 'Quoted by scope' } },
    { slug: 'incident-response', zh: { name: '应急响应保留', price: '¥5万 起 / 年' }, en: { name: 'Incident response retainer', price: 'from $7k / year' } },
    { slug: null, zh: { name: '员工防钓鱼训练', price: '¥1万 起 / 次' }, en: { name: 'Anti-phishing training', price: 'from $1.5k / session' } },
  ],
  faq: {
    zh: [
      { q: '怎么选套餐？', a: '不确定的话先约免费咨询或做一次体检。20 人以内、主要风险在电脑和账号的团队选基础版；有生产服务器、链上资产和对外服务的团队选专业版；交易所、钱包服务商选企业版。' },
      { q: '第三方安全产品的费用包含在内吗？', a: '不包含。EDR、WAF 等第三方产品的授权费按实际采购另算，我们负责选型、部署和策略调优，并帮你谈到合理价格。' },
      { q: '可以按月取消或升级吗？', a: '订阅按月计费，可以随时升级；降级或取消提前一个月告知即可。' },
      { q: '支持哪些付款方式？', a: '支持人民币对公转账、美元，以及 USDT / USDC。' },
      { q: '需要把私钥交给你们吗？', a: '不需要，也不应该。我们只做架构、流程和配置，私钥始终由你们自己保管。' },
    ],
    en: [
      { q: 'How do I choose a plan?', a: 'If unsure, start with a free consultation or an assessment. Teams under 20 whose main risks are devices and accounts fit Basic; teams with production servers, on-chain assets and public services fit Pro; exchanges and wallet providers fit Enterprise.' },
      { q: 'Are third-party security tools included?', a: 'No. Licenses for tools such as EDR or WAF are billed at cost. We handle selection, rollout and tuning, and help you get a fair price.' },
      { q: 'Can I cancel or upgrade monthly?', a: 'Subscriptions are billed monthly. Upgrade anytime; downgrades or cancellations need one month\'s notice.' },
      { q: 'Which payment methods do you accept?', a: 'Bank transfer in CNY or USD, and USDT / USDC.' },
      { q: 'Do you need our private keys?', a: 'No, and you should never share them. We work on architecture, process and configuration; keys always stay with you.' },
    ],
  },
}

export const PAGE_META = {
  pricing: {
    zh: { title: '价格与套餐对比｜区块链企业安全服务 - 牧色安全', description: '免费安全咨询、资产安全体检和安全合伙人订阅（基础版 / 专业版 / 企业版）的价格与能力对比：终端、零信任、内网、WAF、容器、链上监控、代码审计、应急响应。' },
    en: { title: 'Pricing & Plan Comparison — Web3 Security Services | Musesec', description: 'Free consultation, security assessment and Security Partner plans (Basic / Pro / Enterprise) compared: endpoint, zero trust, network, WAF, containers, on-chain monitoring, code audit and incident response.' },
  },
  solutions: {
    zh: { title: '解决方案｜交易所、DeFi、量化做市、OTC、钱包与基金的安全防护 - 牧色安全', description: '按客户类型提供链上 + 链下一体化安全防护：中心化交易所、DeFi 项目方、量化做市商、OTC 支付、钱包服务商、基金、链上工作室与 Web3 创业团队。' },
    en: { title: 'Solutions — Security for Exchanges, DeFi, Market Makers, OTC, Wallets & Funds | Musesec', description: 'On-chain + off-chain protection by customer type: exchanges, DeFi protocols, quant and market makers, OTC and payments, wallet providers, funds, on-chain studios and Web3 startups.' },
  },
}
